import React, { useState, useEffect } from 'react';
import { Modal, Button, Input } from '../_custom';
import styles from './SaveViewModal.module.scss';

interface SaveViewModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (viewName: string) => void;
    existingViewNames: string[];
    isLoading?: boolean;
    isUpdating?: boolean;
    currentViewName?: string;
}

const SaveViewModal: React.FC<SaveViewModalProps> = ({
    isOpen,
    onClose,
    onSave,
    existingViewNames,
    isLoading = false,
    isUpdating = false,
    currentViewName = '',
}) => {
    const [viewName, setViewName] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setViewName(currentViewName);
            setError('');
            // Focus the input when modal opens
            setTimeout(() => {
                const input = document.querySelector('input[name="viewName"]') as HTMLInputElement;
                if (input) {
                    input.focus();
                }
            }, 100);
        }
    }, [isOpen, currentViewName]);

    const validateViewName = (name: string): string => {
        if (!name.trim()) {
            return 'View name is required';
        }
        if (name.trim().length > 50) {
            return 'View name must be less than 50 characters';
        }
        if (existingViewNames.includes(name.trim()) && name.trim() !== currentViewName) {
            return 'A view with this name already exists';
        }
        return '';
    };
    function getButtonText() {
        return isUpdating ? 'Update & Apply' : 'Save & Apply';
    }

    const handleSave = () => {
        const trimmedName = viewName.trim();
        const validationError = validateViewName(trimmedName);

        if (validationError) {
            setError(validationError);
            return;
        }

        onSave(trimmedName);
    };

    const handleInputChange = (value: string) => {
        setViewName(value);

        // Clear error when user starts typing
        if (error) {
            setError('');
        }
    };

    const handleKeyPress = () => {
        if (!error && viewName.trim()) {
            handleSave();
        }
    };

    const isDisabled = !viewName.trim() || !!error || isLoading;

    return (
        <Modal open={isOpen} onClose={onClose} size="flexible">
            <div className={styles.SaveViewModal}>
                <div className={styles.ModalHeader}>
                    <h3>{isUpdating ? 'Update View' : 'Save View'}</h3>
                </div>

                <div className={styles.InputSection}>
                    <Input
                        name="viewName"
                        type="text"
                        value={viewName}
                        onChange={handleInputChange}
                        onEnter={handleKeyPress}
                        placeholder="Enter view name"
                        isValid={!error}
                        errorMessage={error}
                        maxLength={50}
                        style={{ margin: 0, height: '44px' }}
                        showFloatingLabel={true}
                        isActive={true}
                    />
                    <span className={styles.CharCount}>{viewName.length}/50 characters</span>
                </div>

                <div className={styles.ButtonSection}>
                    <Button text="Cancel" variant="secondary" onClick={onClose} style={{ minWidth: '100px' }} />
                    <Button
                        text={isLoading ? 'Saving...' : getButtonText()}
                        variant="primary"
                        onClick={handleSave}
                        disabled={isDisabled}
                        style={{ minWidth: '120px' }}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default SaveViewModal;
